/**
 * 監査ログ(登録・編集ログ)。誰がいつ何を変更したかを追記のみで残す(NEXUS Gate と同方式)。
 * セキュリティルール上、audit_logs は作成のみ許可(更新・削除は誰にもできない)。
 *
 * 記録に失敗しても業務処理は止めない。失敗はコンソールに出す。
 */
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { getDb } from './firebase';
import type { AuditLog, AuditTargetType } from './types';

export async function writeAuditLog(params: {
  targetType: AuditTargetType;
  targetId: string;
  action: string;
  by: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  /** 画面で作った「項目: 旧 → 新」の文章。変更履歴の表示に使う */
  changes?: string[];
}): Promise<void> {
  try {
    await addDoc(collection(getDb(), 'audit_logs'), {
      target_type: params.targetType,
      target_id: params.targetId,
      action: params.action,
      by: params.by,
      at: serverTimestamp(),
      before: params.before ?? null,
      after: { ...(params.after ?? {}), ...(params.changes ? { changes: params.changes } : {}) },
    });
  } catch (e) {
    console.error('監査ログの記録に失敗しました', e);
  }
}

/** 対象1件の変更履歴。新しいものが先 */
export async function fetchAuditLogs(targetType: AuditTargetType, targetId: string, max = 50): Promise<AuditLog[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'audit_logs'), where('target_type', '==', targetType), where('target_id', '==', targetId)),
  );
  const at = (l: AuditLog) => (l.at && typeof l.at.toMillis === 'function' ? l.at.toMillis() : 0);
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as object) }) as AuditLog)
    .sort((a, b) => at(b) - at(a))
    .slice(0, max);
}

/** 変更履歴の1行の文章(changes があればそれを使う) */
export function auditText(log: AuditLog): string {
  const changes = (log.after as { changes?: unknown } | null)?.changes;
  if (Array.isArray(changes) && changes.length > 0) return changes.map(String).join(' ／ ');
  if (log.action.endsWith('-create')) return '登録';
  if (log.action.endsWith('-delete')) return '削除';
  if (log.action.endsWith('-import')) return 'CSVインポートで登録';
  return '設定を変更(内容の記録なし)';
}

/** 「項目: 旧 → 新」の差分文章を組み立てる補助 */
export function diffLines(): { pair: (label: string, a: string, b: string) => void; lines: string[] } {
  const lines: string[] = [];
  return {
    lines,
    pair: (label, a, b) => {
      if (a !== b) lines.push(`${label}: ${a || '—'} → ${b || '—'}`);
    },
  };
}

/** 日時の表示(登録日・変更履歴) */
export function fmtTs(ts: unknown, withTime = true): string {
  const d = ts && typeof (ts as { toDate?: () => Date }).toDate === 'function' ? (ts as { toDate: () => Date }).toDate() : null;
  if (!d) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
  return withTime ? `${date} ${p(d.getHours())}:${p(d.getMinutes())}` : date;
}
